import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Pagination,
  Tooltip,
  Menu,
  useTheme,
  Breadcrumbs,
  Link,
  InputAdornment,
  Divider,
  Stack,
  Badge
} from '@mui/material';
import {
  Refresh,
  MoreVert,
  Visibility,
  Edit,
  CheckCircle,
  Cancel,
  Pending,
  Warning,
  Person,
  Phone,
  Business,
  Inventory,
  CalendarToday,
  Percent,
  AccessTime,
  Home,
  AdminPanelSettings,
  Assignment,
  TrendingUp,
  TrendingDown,
  Straighten,
  NavigateNext,
  LocationOn
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import lotRequestService from '../../services/lotRequestService';
import { productService } from '../../services/productService';

const AdminLotRequestsPage = () => {
  const theme = useTheme();
  const { user } = useSelector((state) => state.auth);
  
  // State declarations - moved before useEffect to avoid initialization errors
  const [lotRequests, setLotRequests] = useState([]);
  const [lotRequestsLoading, setLotRequestsLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [productDetails, setProductDetails] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch lot requests from API
  useEffect(() => {
    const fetchLotRequests = async () => {
      setLotRequestsLoading(true);
      try {
        const response = await lotRequestService.getAllLotRequests(page - 1, rowsPerPage);
        setLotRequests(response.content || response || []);
      } catch (error) {
        console.error('Error fetching lot requests:', error);
        toast.error('Failed to fetch lot requests');
        // Initialize demo data if no requests found
        try {
          await lotRequestService.initializeDemoData();
          const demoResponse = await lotRequestService.getAllLotRequests(page - 1, rowsPerPage);
          setLotRequests(demoResponse.content || demoResponse || []);
        } catch (demoError) {
          console.error('Error initializing demo data:', demoError);
        }
      } finally {
        setLotRequestsLoading(false);
      }
    };

    fetchLotRequests();
  }, [page, rowsPerPage]);

  // Only 6 admin-allowed status fields
  const statusConfig = {
    PENDING_INSPECTION: { 
      label: "Pending Inspection", 
      color: "#FFA726", 
      bgColor: "#FFF3E0", 
      icon: <AccessTime sx={{ color: "#FFA726" }} /> 
    },
    LOT_APPROVED: { 
      label: "Lot Approved", 
      color: "#4CAF50", 
      bgColor: "#E8F5E9", 
      icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
    },
    LOT_REJECTED: { 
      label: "Lot Rejected", 
      color: "#F44336", 
      bgColor: "#FFEBEE", 
      icon: <Cancel sx={{ color: "#F44336" }} /> 
    },
    READY_FOR_DELIVERY: { 
      label: "Ready for Delivery", 
      color: "#2196F3", 
      bgColor: "#E3F2FD", 
      icon: <TrendingUp sx={{ color: "#2196F3" }} /> 
    },
    DELIVERED: { 
      label: "Delivered", 
      color: "#4CAF50", 
      bgColor: "#E8F5E9", 
      icon: <CheckCircle sx={{ color: "#4CAF50" }} /> 
    },
    CANCELLED: { 
      label: "Cancelled", 
      color: "#9E9E9E", 
      bgColor: "#F5F5F5", 
      icon: <Cancel sx={{ color: "#9E9E9E" }} /> 
    }
  };



  // Filter lot requests by status
  const filteredRequests = statusFilter === 'all' 
    ? lotRequests 
    : lotRequests.filter(req => {
        const reqStatus = req.status?.toUpperCase();
        const filterStatus = statusFilter.toUpperCase();
        return reqStatus === filterStatus || 
               reqStatus === filterStatus.replace('_', '') ||
               reqStatus === filterStatus.replace('-', '_');
      });

  // Pagination
  const paginatedRequests = filteredRequests.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Statistics
  const getStats = () => {
    return {
      total: lotRequests.length,
      pending: lotRequests.filter(req => req.status === "pending_inspection").length,
      approved: lotRequests.filter(req => req.status === "lot_approved").length,
      rejected: lotRequests.filter(req => req.status === "lot_rejected").length,
      booked: lotRequests.filter(req => req.status === "BOOKED").length
    };
  };

  const stats = getStats();

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await lotRequestService.updateStatus(requestId, newStatus, user?.id || 'admin', 'Status updated by admin');
      setLotRequests(prev => 
        prev.map(req => 
          req.id === requestId ? { ...req, status: newStatus } : req
        )
      );
      toast.success('Lot request status updated successfully!');
    } catch (error) {
      console.error('Error updating lot request status:', error);
      toast.error('Failed to update lot request status. Please try again.');
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
    setCurrentImageIndex(0);
  };

  // Fetch product details when selectedRequest changes
  useEffect(() => {
    const fetchProductDetails = async () => {
      if (selectedRequest && selectedRequest.productId) {
        setProductLoading(true);
        try {
          const product = await productService.getProductById(selectedRequest.productId);
          setProductDetails(product.product || product);
        } catch (error) {
          console.error('Error fetching product details:', error);
          setProductDetails(null);
        } finally {
          setProductLoading(false);
        }
      } else {
        setProductDetails(null);
      }
    };

    fetchProductDetails();
  }, [selectedRequest]);

  // Get all product images (primary first, then others)
  const getProductImages = () => {
    if (!productDetails) return [];
    
    const images = [];
    
    // Add primary image first if available
    if (productDetails.primaryImageUrl) {
      images.push(productDetails.primaryImageUrl);
    }
    
    // Add images from imageUrls array (if exists)
    if (productDetails.imageUrls && Array.isArray(productDetails.imageUrls)) {
      productDetails.imageUrls.forEach(imageUrl => {
        if (imageUrl && imageUrl !== productDetails.primaryImageUrl) {
          images.push(imageUrl);
        }
      });
    }
    
    // Add other images from images array
    if (productDetails.images && Array.isArray(productDetails.images)) {
      productDetails.images.forEach(img => {
        const imageUrl = typeof img === 'string' ? img : (img.url || img.imageUrl);
        if (imageUrl && imageUrl !== productDetails.primaryImageUrl && !images.includes(imageUrl)) {
          images.push(imageUrl);
        }
      });
    }
    
    // If no images found, return empty array
    return images;
  };

  const productImages = getProductImages();

  const handleNextImage = () => {
    if (productImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }
  };



  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/admin"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Admin
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <Assignment sx={{ mr: 0.5 }} fontSize="inherit" />
          Lot Requests
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Lot Request Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor and manage all lot requests across the platform
        </Typography>
      </Box>

      {/* Statistics Cards - 5 cards with equal dimensions in one row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            height: 120,
            minHeight: 120,
            maxHeight: 120,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 40, height: 40 }}>
                  <Assignment />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                    Total Requests
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                    {stats.total}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            height: 120,
            minHeight: 120,
            maxHeight: 120,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 40, height: 40 }}>
                  <Cancel />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                    Cancelled
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                    {lotRequests.filter(req => req.status === "CANCELLED").length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            height: 120,
            minHeight: 120,
            maxHeight: 120,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2, width: 40, height: 40 }}>
                  <AccessTime />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                    Pending Inspection
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                    {lotRequests.filter(req => req.status === "PENDING_INSPECTION" || req.status === "pending_inspection").length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            height: 120,
            minHeight: 120,
            maxHeight: 120,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2, width: 40, height: 40 }}>
                  <CheckCircle />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                    Delivered
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                    {lotRequests.filter(req => req.status === "DELIVERED").length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4} lg={2.4} xl={2.4}>
          <Card sx={{ 
            border: '2px solid #1e3a8a', 
            borderRadius: 2,
            height: 120,
            minHeight: 120,
            maxHeight: 120,
            '&:hover': { 
              boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.2s ease-in-out'
            }
          }}>
            <CardContent sx={{ py: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2, width: 40, height: 40 }}>
                  <TrendingUp />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                    Ready for Delivery
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
                    {lotRequests.filter(req => req.status === "READY_FOR_DELIVERY").length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>



      {/* Data Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Lot Requests ({filteredRequests.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {Object.entries(statusConfig).map(([status, config]) => (
                    <MenuItem key={status} value={status}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {config.icon}
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                startIcon={<Refresh />}
                onClick={() => {
                  window.location.reload();
                }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {lotRequestsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>Loading lot requests...</Typography>
            </Box>
          ) : paginatedRequests.length > 0 ? (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Seller</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Advance Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRequests.map((request) => (
                      <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {request.customerName || request.customer?.name || 'Unknown Customer'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                                {request.customerPhone || request.customer?.phone || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                              <Business />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {request.sellerName || request.seller?.name || 'Unknown Seller'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                                {request.sellerPhone || request.seller?.phone || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {request.productName || request.product?.name || 'Unknown Product'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {request.productDetails || request.product?.description || request.message || 'No details'}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            ₹{request.advanceAmount ? request.advanceAmount.toLocaleString() : '0'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Advance Payment
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={statusConfig[request.status]?.label || request.status || 'Unknown'}
                            sx={{
                              backgroundColor: statusConfig[request.status]?.bgColor || '#f5f5f5',
                              color: statusConfig[request.status]?.color || '#666',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                            icon={statusConfig[request.status]?.icon}
                          />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(request)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select
                                value={request.status}
                                onChange={(e) => handleStatusUpdate(request.id, e.target.value)}
                                displayEmpty
                              >
                                {Object.entries(statusConfig).map(([status, config]) => (
                                  <MenuItem key={status} value={status}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {config.icon}
                                      {config.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(filteredRequests.length / rowsPerPage)}
                  page={page}
                  onChange={(event, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Assignment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No lot requests found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || statusFilter !== "all" || priorityFilter !== "all"
                  ? 'Try adjusting your filters or search terms.'
                  : 'There are no lot requests at the moment.'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            Lot Request Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={3} sx={{ pt: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Customer Information
                </Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedRequest.customerName}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {selectedRequest.customerPhone}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {selectedRequest.customerEmail}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Seller Information
                </Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedRequest.sellerName}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {selectedRequest.sellerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Product & Lot Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Lot ID:</strong> {selectedRequest._id || selectedRequest.id || 'N/A'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Product:</strong> {selectedRequest.productName}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Details:</strong> {selectedRequest.productDetails}
                    </Typography>
                    {productLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading product details...</Typography>
                      </Box>
                    ) : productDetails && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Origin:</strong> {productDetails.origin || 'N/A'}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Lot Size:</strong> {selectedRequest.lotSize}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Lot Percentage:</strong> {selectedRequest.lotPercentage}%
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Timeline:</strong> {selectedRequest.timeline || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {productLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2" color="text.secondary">Loading images...</Typography>
                      </Box>
                    ) : productImages.length > 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          Product Images:
                        </Typography>
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box
                            component="img"
                            src={productImages[currentImageIndex]?.startsWith('http') 
                              ? productImages[currentImageIndex] 
                              : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8083'}${productImages[currentImageIndex]}`}
                            alt={`Product image ${currentImageIndex + 1}`}
                            sx={{
                              width: '100%',
                              maxWidth: 400,
                              height: 'auto',
                              maxHeight: 300,
                              objectFit: 'contain',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              display: 'block'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          {productImages.length > 1 && (
                            <Button
                              variant="contained"
                              size="small"
                              endIcon={<NavigateNext />}
                              onClick={handleNextImage}
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                minWidth: 'auto',
                                px: 1.5
                              }}
                            >
                              Next ({currentImageIndex + 1}/{productImages.length})
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                        No images available
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Customer Message
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  "{selectedRequest.message}"
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminLotRequestsPage;